import { Card } from './ui/card'
import { Skeleton } from './ui/skeleton'
import { cn } from '@/lib/utils'
import type { RosterSummary } from './RosterCalendarView'

interface RosterDutyStatusKeyProps {
  summary: RosterSummary
  className?: string
}

interface DutyStatusKeyItem {
  count: number
  label: string
  colorClass: string
}

/**
 * Footer legend for the roster views. Shows a colored square + label per duty
 * status, with no counts, and only for statuses that actually have entries.
 */
export function RosterDutyStatusKey({ summary, className }: RosterDutyStatusKeyProps) {
  const items: DutyStatusKeyItem[] = [
    { count: summary.onDuty, label: 'On Duty', colorClass: 'bg-status-on-duty' },
    { count: summary.offDuty, label: 'Off Duty', colorClass: 'bg-status-off-duty' },
    { count: summary.standby, label: 'Standby', colorClass: 'bg-status-standby' },
    { count: summary.training, label: 'Training', colorClass: 'bg-status-training' },
    { count: summary.leave, label: 'Leave', colorClass: 'bg-status-leave' },
    { count: summary.sick, label: 'Sick', colorClass: 'bg-status-sick' },
    { count: summary.positioning, label: 'Positioning', colorClass: 'bg-status-positioning' },
  ].filter((item) => item.count > 0)

  if (items.length === 0) return null

  return (
    <Card
      className={cn(
        'flex items-center justify-center gap-3 h-12 px-6 py-3',
        className
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2 py-1">
          <div className={cn('rounded-[4px] size-3 shrink-0', item.colorClass)} />
          <span className="text-xs font-semibold text-content-primary-subtle leading-4 tracking-[0px]">
            {item.label}
          </span>
        </div>
      ))}
    </Card>
  )
}

/** Loading placeholder matching the RosterDutyStatusKey footer layout. */
export function RosterKpiCardSkeleton({ className }: { className?: string }) {
  return (
    <Card
      className={cn(
        'flex items-center justify-center gap-3 h-12 px-6 py-3',
        className
      )}
    >
      {[0, 1, 2].map((index) => (
        <div key={index} className="flex items-center gap-2 py-1">
          <Skeleton className="size-3 rounded-[4px] shrink-0" />
          <Skeleton className="h-3 w-12" />
        </div>
      ))}
    </Card>
  )
}

/** Simple shared content placeholder reused by the roster Plan/Calendar/List views. */
export function RosterContentSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-lg" />
      ))}
    </div>
  )
}
